import { useDispatch } from 'react-redux'
import { Redirect } from 'react-router'
import { Router } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
import { Helmet } from 'react-helmet-async'
import classnames from 'classnames'
import { parse } from 'qs'

import 'i18n'

import { pathLanguageSelector } from 'i18n/selectors'
import { stripLanguage, createPath } from 'i18n/utils'
import { messages } from '../i18n'
import {
    wordpressLanguageSelector,
    configurationPendingSelector,
    configurationErrorSelector,
    languagesSelector,
} from 'wordpress/selectors'
import Favicon from 'favicon'

import Routes from './components/Routes'
import PleaseWait from './components/PleaseWait'
import '../bootstrap/fonts.scss'
import '../bootstrap/bootstrap.scss'
import { pathSelector } from 'browser-location'
import { isAuthVisibleSelector } from 'header/selectors'
import NotFound from '../not-found/NotFound'
import Button from '@components/Button'
import { getActiveLocale } from 'shared/country'
import { useGoogleRecaptcha } from 'core/hooks'
import featureToggles from 'shared/featureToggles'
import { loadValidations } from 'shared/validations'
import CookieModal from 'cookies/components/CookieModal'
import { getHandleReactIntlOnError } from './devUtils'
import { openCookiesModal } from 'cookies/actions'

import styles from './components/styles.module.scss'

const fetchTranslations = async locale => {
    try {
        const response = await fetch(`/locale/${locale}.json`)
        const json = await response.json()
        return json
    } catch (err) {
        console.error(err.message)
        return false
    }
}

const App = ({ history, language, languages, wordpressLanguage, pending, path, isAuthVisible, configurationError }) => {
    const dispatch = useDispatch()

    useGoogleRecaptcha()
    const [translationStrings, setTranslationStrings] = React.useState({})
    const locale = getActiveLocale(languages)

    async function loadTranslations() {
        const translations = await fetchTranslations(locale)
        setTranslationStrings(translations)
    }

    React.useEffect(() => {
        loadTranslations()
        loadValidations()
    }, [locale])

    return (
        <IntlProvider
            locale={locale}
            messages={messages[locale] || translationStrings}
            onError={getHandleReactIntlOnError()}>
            {configurationError ? (
                <NotFound status={<T>server.error.title</T>} message={<T>server.error.message</T>}>
                    <Button size="large" className="mt-5" onClick={() => location.reload()}>
                        <T>server.error.button-text</T>
                    </Button>
                </NotFound>
            ) : (
                <div id="pageScrollWrapper" style={{ height: '100vh', overflow: isAuthVisible ? 'hidden' : 'scroll' }}>
                    <Favicon />
                    <Helmet>
                        <title>Indigo</title>
                    </Helmet>
                    {!pending ? (
                        <>
                            {featureToggles.showOnScreenCookieButton && (
                                <Button
                                    iconLeft="cookies"
                                    onClick={() => dispatch(openCookiesModal())}
                                    className={classnames(styles.cookieButton, 'text-small')}>
                                    <T>cookie.btn</T>
                                </Button>
                            )}
                            {featureToggles.showCookiePolicy && <CookieModal />}
                            <Router history={history}>
                                {wordpressLanguage === language ? (
                                    <Routes />
                                ) : (
                                    <Redirect
                                        replace
                                        to={createPath(
                                            wordpressLanguage,
                                            stripLanguage(`${path}${history.location.hash}`),
                                            parse(history.location.search)
                                        )}
                                    />
                                )}
                            </Router>
                        </>
                    ) : (
                        <PleaseWait />
                    )}
                </div>
            )}
        </IntlProvider>
    )
}

export default connect(
    createStructuredSelector({
        languages: languagesSelector,
        language: pathLanguageSelector,
        wordpressLanguage: wordpressLanguageSelector,
        pending: configurationPendingSelector,
        configurationError: configurationErrorSelector,
        path: pathSelector,
        isAuthVisible: isAuthVisibleSelector,
    })
)(App)
